import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  ExpenseReceipt,
  ExpenseReceiptBatchCommand,
  ExpenseReceiptBatchItem,
  ExpenseReceiptOwner,
  ExpenseReceiptPool,
  ExpenseReceiptVendor,
  OwnerReference,
} from '../models/expense-receipt/expense-receipt';

@Injectable({ providedIn: 'root' })
export class ExpenseReceiptProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/expense-receipt';

  /** The funds and trips the caller may capture receipts for. */
  public getOwners(): Observable<ApiResponse<ExpenseReceiptOwner[]>> {
    return this.httpClient.get<ApiResponse<ExpenseReceiptOwner[]>>(`${this.baseUrl}/owners`);
  }

  public getVendors(term: string): Observable<ApiResponse<ExpenseReceiptVendor[]>> {
    return this.httpClient.get<ApiResponse<ExpenseReceiptVendor[]>>(`${this.baseUrl}/vendors`, { params: { term } });
  }

  public getPool(owner: OwnerReference): Observable<ApiResponse<ExpenseReceiptPool>> {
    return this.httpClient.get<ApiResponse<ExpenseReceiptPool>>(`${this.baseUrl}/pool`, {
      params: { ownerReference: owner.reference, ownerCode: owner.entityDefinitionCode },
    });
  }

  /** Saves several receipts; each item reports its own success or error. */
  public saveBatch(command: ExpenseReceiptBatchCommand): Observable<ApiResponse<ExpenseReceiptBatchItem[]>> {
    return this.httpClient.post<ApiResponse<ExpenseReceiptBatchItem[]>>(`${this.baseUrl}/batch`, command);
  }

  /** One Incomplete receipt per file. */
  public upload(owner: OwnerReference, files: File[]): Observable<ApiResponse<ExpenseReceipt[]>> {
    const form = new FormData();
    form.append('ownerReference', owner.reference);
    form.append('ownerCode', String(owner.entityDefinitionCode));
    files.forEach((file) => form.append('files', file));
    return this.httpClient.post<ApiResponse<ExpenseReceipt[]>>(`${this.baseUrl}/upload`, form);
  }

  public addAttachment(id: string, file: File): Observable<ApiResponse<ExpenseReceipt>> {
    const form = new FormData();
    form.append('file', file);
    return this.httpClient.post<ApiResponse<ExpenseReceipt>>(`${this.baseUrl}/${id}/attachment`, form);
  }

  public removeAttachment(id: string, name: string): Observable<ApiResponse<ExpenseReceipt>> {
    return this.httpClient.delete<ApiResponse<ExpenseReceipt>>(`${this.baseUrl}/${id}/attachment`, { params: { name } });
  }

  public changeOwner(receiptIds: string[], owner: OwnerReference): Observable<ApiResponse<number>> {
    return this.httpClient.post<ApiResponse<number>>(`${this.baseUrl}/change-owner`, { receiptIds, owner });
  }

  public delete(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${id}`);
  }
}
